import { Brands, LegacyBrands } from '@framework/brands';
import { Environments } from './generalEnums.js';
export function getImmutableConfig(mutableConfig) {
    return deepFreeze(mutableConfig);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepFreeze(object) {
    Object.getOwnPropertyNames(object).forEach((name) => {
        const value = object[name];
        if (value && (typeof value === 'object' || typeof value === 'function') && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    });
    return Object.freeze(object);
}
function extendEnv(envConfig, baseConfig) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = {};
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const key in { ...baseConfig, ...envConfig }) {
        if (typeof baseConfig[key] === 'object' &&
            !Array.isArray(baseConfig[key]) &&
            baseConfig[key] !== null &&
            envConfig &&
            key in envConfig) {
            const deepEnvConfig = envConfig[key];
            const deepBaseConfig = baseConfig[key];
            result[key] = extendEnv(deepEnvConfig, deepBaseConfig);
        }
        else if (envConfig && key in envConfig && typeof envConfig[key] === 'function') {
            result[key] = envConfig[key].bind(result);
        }
        else if (typeof baseConfig[key] === 'function') {
            result[key] = baseConfig[key].bind(result);
        }
        else if (envConfig && key in envConfig) {
            result[key] = envConfig[key];
        }
        else {
            result[key] = baseConfig[key];
        }
    }
    return result;
}
export function getEnvVariableStrict(variable, defaultValue) {
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process !== 'undefined') {
        const resolved = process.env[variable];
        if (resolved !== undefined) {
            return resolved;
        }
        else if (resolved === undefined && defaultValue) {
            return defaultValue;
        }
        else {
            if (process.env.APP_ENV !== 'test' && process.env.APP_ENV !== 'local') {
                throw new Error(`Exprected env variable ${variable} was not provided`);
            }
            return '';
        }
    }
    return '';
}
export function getEnvVariableDefault(variable, defaultValue) {
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process !== 'undefined') {
        const resolved = process.env[variable];
        if (resolved !== undefined) {
            return resolved;
        }
    }
    return defaultValue;
}
export function getEnvVariable(variable) {
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process !== 'undefined') {
        const resolved = process.env[variable];
        if (resolved !== undefined) {
            return resolved;
        }
    }
    return undefined;
}
function _isValidEnvValue(v) {
    return Object.values(Environments).find((e) => e === v) !== undefined;
}
export function getEnvironmentName() {
    const environment = process.env.APP_ENV;
    if (environment === undefined) {
        throw new Error('APP_ENV is undefined.\nHint: configuration should be initialized lazily during runtime and not during build time.');
    }
    if (!_isValidEnvValue(environment)) {
        throw new Error(`APP_ENV=${environment}. Allowed values=${Object.values(Environments).join(', ')}`);
    }
    return environment;
}
/** Cache keyed by config definition object reference. WeakMap allows entries to be garbage collected when the config is no longer referenced; no TTL or eviction needed for read-only config. */
let envConfigCache = new WeakMap();
export function getEnvironmentConfig(configDefinition) {
    const cached = envConfigCache.get(configDefinition);
    if (cached !== undefined) {
        return cached;
    }
    const { base, ...availableConfigs } = configDefinition;
    let envConfig;
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process === 'undefined') {
        envConfig = availableConfigs.local;
    }
    else {
        const environment = getEnvironmentName();
        envConfig = availableConfigs[environment];
    }
    const result = getImmutableConfig(envConfig ? extendEnv(envConfig, base) : base);
    envConfigCache.set(configDefinition, result);
    return result;
}
/**
 * Cache keyed by (config definition, brand): config definition is the WeakMap key; brand is the key of the inner Map.
 * WeakMap keys must be objects, and a new key object per lookup would never match, so we use this two-level structure.
 * WeakMap avoids strong retention of config definitions once unused.
 */
let brandedEnvConfigCache = new WeakMap();
/** Cached result of env+base merge per config def so the first extendEnv runs once per config def, not per brand. */
let brandedEnvMergedCache = new WeakMap();
/**
 * Reset cached environment configs.
 * Useful in tests that patch config definitions at runtime.
 */
export function resetEnvironmentConfigCache() {
    envConfigCache = new WeakMap();
    brandedEnvConfigCache = new WeakMap();
    brandedEnvMergedCache = new WeakMap();
}
/**
 * Extract the Environment and Brand specific config
 */
export function getBrandedEnvironmentConfig(configDefinition, brand) {
    let cachedConfigByBrand = brandedEnvConfigCache.get(configDefinition);
    const cached = cachedConfigByBrand?.get(brand);
    if (cached !== undefined) {
        return cached;
    }
    const { base, ...availableConfigs } = configDefinition;
    let envConfig;
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process === 'undefined') {
        envConfig = availableConfigs.local;
    }
    else {
        const environment = getEnvironmentName();
        // Pick the APP_ENV environment from available configs
        envConfig = availableConfigs[environment];
    }
    // Get or compute env+base merge (cached per config def)
    let merged = brandedEnvMergedCache.get(configDefinition);
    if (merged === undefined) {
        merged = envConfig ? extendEnv(envConfig, base) : base;
        brandedEnvMergedCache.set(configDefinition, merged);
    }
    const { _, ...brandedConfigs } = merged;
    // Pick the brand from the merged environment + base config
    const brandConfig = brandedConfigs[brand];
    const result = !brandConfig ? getImmutableConfig(_) : getImmutableConfig(extendEnv(brandConfig, _));
    if (!cachedConfigByBrand) {
        cachedConfigByBrand = new Map();
        brandedEnvConfigCache.set(configDefinition, cachedConfigByBrand);
    }
    cachedConfigByBrand.set(brand, result);
    return result;
}
/**
 * Extract the Brand specific config WITHOUT Environment
 */
export function getBrandedConfig(configDefinition, brand) {
    // Split the base unbranded config and the branded overrides
    const { _, ...brandedConfigs } = configDefinition;
    // Pick the brand overrides
    const brandConfig = brandedConfigs[brand];
    if (!brandConfig)
        return getImmutableConfig(_);
    // Extend the generic unbranded config by the brand overrides
    return getImmutableConfig(extendEnv(brandConfig, _));
}
/**
 * for feature branch sandboxes = `CTF-123.` (branch name with dot at the end)
 * for main sandbox = '' (empty string)
 */
export function getFeatureBranchPrefix() {
    // CTF-4933 fixing tree-shaking & hydration issues
    if (typeof process === 'undefined') {
        return '';
    }
    return process.env.BRANCH_SANDBOX_NAME ? `${process.env.BRANCH_SANDBOX_NAME}.` : '';
}
/**
 * Raw feature branch prefix.
 * for feature branch sandboxes = `CTF-123`
 * for main sandbox = '' (empty string)
 */
export function getFeatureBranchPrefixRaw() {
    return process.env.BRANCH_SANDBOX_NAME ?? '';
}
/**
 * for feature branch sandboxes = true (branch name with dot at the end)
 * for main sandbox = false (empty string)
 */
export function isFeatureBranchEnv() {
    return getFeatureBranchPrefix() !== '';
}
function isBrandKey(k) {
    return Object.keys(Brands).includes(k);
}
export function getBrandByKey(k) {
    return isBrandKey(k) ? Brands[k] : undefined;
}
export function getBrandByValue(v) {
    return v ? Object.values(Brands).find((b) => b === v) : undefined;
}
export function getBrandByValueOrFail(v) {
    const brand = getBrandByValue(v);
    if (!brand) {
        throw new Error('invalid brand!');
    }
    return brand;
}
const isLegacyBrandKey = (k) => {
    return Object.keys(LegacyBrands).includes(k);
};
export function getLegacyBrandByKey(k) {
    return isLegacyBrandKey(k) ? LegacyBrands[k] : undefined;
}
/**
 * Type guard that verifies a partially built brand config contains keys for all Brands.
 * Useful when constructing brand-specific maps incrementally and asserting completeness.
 */
export function hasConfigAllBrandKeys(config) {
    return Object.values(Brands).every((brand) => config[brand] !== undefined);
}
//# sourceMappingURL=configHelper.js.map